import {
  Header,
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '@getpopsure/qnr-framework';
import { Button } from '@popsure/dirty-swan';
import { useFlag } from '@unleash/proxy-client-react';
import { setAccountDataInQuestionnaire } from 'actions/account';
import classNames from 'classnames';
import {
  GoogleError,
  Input,
  Title,
} from 'components/AccountEmail/components/Email/Email';
import { GoogleLoginButton } from 'features/GoogleOAuth';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'reducers';
import { GenericQuestionnaireState } from 'reducers/genericQuestionnaire';
import { getRequestError, getRequestInProcess } from 'selectors/requests';
import { getEmail } from 'selectors/user';
import { useSafeTranslation } from 'shared/i18n';
import { useEmailQuestion } from 'SignupQuestionnaire/components//Email/useEmailQuestion';
import { Email as EmailV2 } from 'SignupQuestionnaire/components/Email/EmailV2';
import LoginModal from 'SignupQuestionnaire/components/Email/LoginModal';

import styles from './Email.module.scss';

export const Email = <Questionnaire extends QuestionnaireQuestions>(
  props: {
    verticalId: keyof GenericQuestionnaireState;
    emailParam?: string | null;
    showTerms?: boolean;
  } & QuestionnaireFormProps<Questionnaire>
) => {
  const {
    value,
    onSubmitValue,
    verticalId,
    setValidForSubmission,
    showTerms = true,
    questionnaireAnswers,
  } = props;
  const { t } = useSafeTranslation();
  const [googleLoginError, setGoogleLoginError] = useState(false);
  const [isLoginWithGoogle, setIsLoginWithGoogle] = useState(false);
  const useNewEmailComponent = useFlag('app_sign_in_flow_email_component_v2');
  const dispatch = useDispatch();
  const accountEmail = useSelector(getEmail);
  const redirectPath =
    typeof questionnaireAnswers.redirectPath === 'string'
      ? questionnaireAnswers.redirectPath
      : '';

  const loadingGoogle = useSelector((state: AppState) =>
    getRequestInProcess(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );
  const errorGoogle = useSelector((state: AppState) =>
    getRequestError(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );

  useEffect(() => {
    if (!errorGoogle && !loadingGoogle && accountEmail && isLoginWithGoogle) {
      setIsLoginWithGoogle(false);
      onSubmitValue(accountEmail);
    }
  }, [
    errorGoogle,
    loadingGoogle,
    accountEmail,
    onSubmitValue,
    isLoginWithGoogle,
  ]);

  const {
    email,
    handleInputChange,
    handleModalClose,
    handleSubmit,
    isValid,
    onLogin,
    requiresLoginCode,
    toggleTermsAccepted,
  } = useEmailQuestion({
    onSubmitValue,
    setValidForSubmission,
    showTerms,
    value,
  });

  const handleOnGoogleLogin = () => {
    dispatch(setAccountDataInQuestionnaire(redirectPath, verticalId));
    setIsLoginWithGoogle(true);
  };

  if (useNewEmailComponent) {
    return <EmailV2 {...props} />;
  }

  return (
    <div className="p-body">
      <div className={styles.container}>
        <Header onSubmit={handleSubmit}>
          <Title
            text={t('components.email.title', "What's your email address?")}
          />
          <Input
            show
            value={(value as string) ?? email}
            emailInputOnChange={handleInputChange}
            tAndCInputOnChange={toggleTermsAccepted}
            showTerms={showTerms}
          />
          <div>{googleLoginError && <GoogleError />}</div>
          <div
            className={classNames(styles.buttonContainer, 'd-flex gap8 mt16')}
          >
            <Button
              disabled={!isValid}
              className="wmn3"
              data-cy="button-continue"
            >
              {t('components.email.button', 'Continue')}
            </Button>
            <GoogleLoginButton
              className="wmn3"
              onSuccess={() => handleOnGoogleLogin()}
              onLoginError={() => {
                setIsLoginWithGoogle(false);
                setGoogleLoginError(true);
              }}
            />
          </div>
        </Header>
      </div>
      <LoginModal
        email={email}
        isOpen={requiresLoginCode}
        onClose={handleModalClose}
        onLogin={onLogin}
        verticalId={verticalId}
      />
    </div>
  );
};
